import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AssistantMailboxesCard from './AssistantMailboxesCard.tsx';
import AssistantTabForm, { TabSection } from './AssistantTabForm.tsx';
import { useAssistantConfigForm } from './useAssistantConfigForm.ts';
import FormField from '../../../../core/components/FormField.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { SettingsCard } from '../../../../core/components/SettingsFormCard.tsx';
import classnames from '../../../../core/utils/classnames.tsx';
import InlineSelect from '../../../../document/components/InlineSelect.tsx';
import { getDocsUrl, OUTLOOK_ADD_IN_DOCS_PATH } from '../../../../support/constants.ts';
import ConsentLinkRow from '../../../mailboxes/components/ConsentLinkRow.tsx';
import useAdminConsentLink from '../../../mailboxes/components/useAdminConsentLink.ts';
import { OUTLOOK_ADD_IN_URL } from '../../constants.ts';
import useTeamOptions from '../../hooks/useTeamOptions.ts';
import AgentDetailsFormFields from '../config/AgentDetailsFormFields.tsx';
import { AGENT_CONFIG_STEPS } from '../config/ConfigSteps.tsx';
import InstructionSections from '../knowledge/InstructionSections.tsx';

const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);

const PREFIX = 'detail.settingsTab';

const DRAFT_TYPES = [
    { value: 'auto', key: 'auto' },
    { value: 'draft_email', key: 'draftEmail' },
    { value: 'summary', key: 'summary' },
];

const LINK_CLASS_NAME =
    'inline-flex items-center gap-1.5 self-start rounded text-sm font-medium text-content-brand-default underline-offset-2 hover:text-content-brand-hover hover:underline active:text-content-brand-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong';

interface SmartInboxTrigger {
    id: string;
    name: string;
    agentIds: string[];
}

interface CardProps {
    agent: any;
    onChange: () => Promise<unknown>;
}

const GeneralCard = ({ agent, onChange }: CardProps) => {
    const { t } = useTranslation('assistants');
    const { form, isSubmitting, onSubmit } = useAssistantConfigForm(agent, onChange);
    const teamOptions = useTeamOptions();

    return (
        <AssistantTabForm
            title={t(`${PREFIX}.general.title`)}
            description={t(`${PREFIX}.general.description`)}
            form={form}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            isValid={AGENT_CONFIG_STEPS.details.isValid!(form)}
        >
            <AgentDetailsFormFields form={form} teamOptions={teamOptions} />
        </AssistantTabForm>
    );
};

const DraftsCard = ({ agent, onChange }: CardProps) => {
    const { t } = useTranslation('assistants');
    const { form, isSubmitting, onSubmit } = useAssistantConfigForm(agent, onChange);

    const draftTypeOptions = DRAFT_TYPES.map(({ value, key }) => ({
        value,
        label: t(`${PREFIX}.drafts.draftTypes.${key}`),
        sample: t(`${PREFIX}.drafts.draftTypes.${key}Description`),
    }));

    return (
        <AssistantTabForm
            title={t(`${PREFIX}.drafts.title`)}
            description={t(`${PREFIX}.drafts.description`)}
            form={form}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            isValid={AGENT_CONFIG_STEPS.delivery.isValid!(form)}
        >
            <Controller
                name="outputInstructions"
                control={form.control}
                render={({ field }) => (
                    <InstructionSections
                        label={t(`${PREFIX}.drafts.writingInstructions`)}
                        helpText={t(`${PREFIX}.drafts.writingInstructionsHelpText`)}
                        value={field.value}
                        onChange={field.onChange}
                    />
                )}
            />

            <Controller
                name="deliveryMethod"
                control={form.control}
                render={({ field }) => (
                    <FormField label={t(`${PREFIX}.drafts.draftType`)} required>
                        {/* the help text sits under the label, as on Writing instructions */}
                        <div className="flex flex-col gap-2">
                            <span className="text-xs text-content-medium">
                                {t(`${PREFIX}.drafts.draftTypeHelpText`)}
                            </span>
                            <InlineSelect
                                value={field.value}
                                onValueChange={(value) => field.onChange(value)}
                                onBlur={field.onBlur}
                                options={draftTypeOptions}
                            />
                        </div>
                    </FormField>
                )}
            />
        </AssistantTabForm>
    );
};

interface OutlookOptionProps {
    title: string;
    description: string;
    href: string;
    linkLabel: string;
}

const OutlookOption = ({ title, description, href, linkLabel }: OutlookOptionProps) => (
    <div className="flex items-center justify-between gap-6">
        <div className="flex min-w-0 flex-col gap-0.5">
            <h3 className="m-0 text-sm font-medium text-content-strong">{title}</h3>
            <p className="m-0 text-sm text-content-medium">{description}</p>
        </div>
        <div className="self-center">
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={classnames(LINK_CLASS_NAME, 'flex-none underline')}
            >
                {linkLabel}
                <ExternalLinkIcon />
            </a>
        </div>
    </div>
);

const OutlookCard = () => {
    const { t, i18n } = useTranslation('assistants');

    return (
        <SettingsCard title={t(`${PREFIX}.outlook.title`)} description={t(`${PREFIX}.outlook.description`)}>
            <OutlookOption
                title={t(`${PREFIX}.outlook.perUserTitle`)}
                description={t(`${PREFIX}.outlook.perUserDescription`)}
                href={OUTLOOK_ADD_IN_URL}
                linkLabel={t(`${PREFIX}.outlook.openMarketplace`)}
            />
            <OutlookOption
                title={t(`${PREFIX}.outlook.orgWideTitle`)}
                description={t(`${PREFIX}.outlook.orgWideDescription`)}
                href={getDocsUrl(i18n.language, OUTLOOK_ADD_IN_DOCS_PATH)}
                linkLabel={t(`${PREFIX}.outlook.orgWideGuide`)}
            />
        </SettingsCard>
    );
};

const Microsoft365Card = () => {
    const { t } = useTranslation('assistants');
    const sharedLink = useAdminConsentLink(false);
    const personalLink = useAdminConsentLink(true);

    return (
        <SettingsCard title={t(`${PREFIX}.microsoft365.title`)} description={t(`${PREFIX}.microsoft365.description`)}>
            <TabSection title={t(`${PREFIX}.microsoft365.shared`)}>
                <ConsentLinkRow link={sharedLink} />
            </TabSection>
            <TabSection
                title={t(`${PREFIX}.microsoft365.personal`)}
                description={t(`${PREFIX}.microsoft365.personalDescription`)}
            >
                <ConsentLinkRow link={personalLink} />
            </TabSection>
        </SettingsCard>
    );
};

interface AssistantSettingsTabProps extends CardProps {
    availableSmartInboxTriggers: SmartInboxTrigger[];
}

/** The Settings tab: the assistant's general details, its mailboxes and drafts, and how to connect Outlook and
 *  Microsoft 365. Each editable card saves on its own. */
const AssistantSettingsTab = ({ agent, onChange, availableSmartInboxTriggers }: AssistantSettingsTabProps) => (
    <div className="flex flex-col gap-6">
        <GeneralCard agent={agent} onChange={onChange} />
        <AssistantMailboxesCard
            agent={agent}
            onChange={onChange}
            availableSmartInboxTriggers={availableSmartInboxTriggers}
        />
        <DraftsCard agent={agent} onChange={onChange} />
        <OutlookCard />
        <Microsoft365Card />
    </div>
);

export default AssistantSettingsTab;
